/**
 * Batch Location Generator Service
 * 
 * Servicio para generar ubicaciones en lote con estructura jerárquica.
 * Soporta 4 profundidades diferentes y nomenclatura personalizable.
 * 
 * @author Hugo Andrés Escobar Ciceri
 * @version 1.0.0
 */

import { Injectable, inject } from '@angular/core';
import {
  BatchCreationConfig,
  LocationDepth,
  NomenclaturePrefixes,
  DEFAULT_PREFIXES,
  getEffectivePrefix,
} from '../models/batch-creation-config.model';
import {
  GeneratedLocation,
  GeneratedLocationsResult,
  LocationType,
  LocationIndices,
  getLocationType,
  sortLocationsByHierarchy,
  groupLocationsByType,
  estimateCreationTime,
} from '../models/generated-location.model';
import {
  formatConsecutive,
  calculateLocations,
} from '../utils/batch-calculation.utils';

/**
 * Servicio para generar ubicaciones en batch
 */
@Injectable({
  providedIn: 'root',
})
export class BatchLocationGeneratorService {
  /**
   * Genera todas las ubicaciones según la configuración
   * 
   * @param config - Configuración de batch creation
   * @returns Resultado con ubicaciones generadas y estadísticas
   */
  generateLocations(config: BatchCreationConfig): GeneratedLocationsResult {
    // Validar configuración
    this.validateConfig(config);

    // Generar según profundidad
    let locations: GeneratedLocation[] = [];

    switch (config.depth) {
      case 1:
        locations = this.generateDepth1(config);
        break;
      case 2:
        locations = this.generateDepth2(config);
        break;
      case 3:
        locations = this.generateDepth3(config);
        break;
      case 4:
        locations = this.generateDepth4(config);
        break;
      default:
        throw new Error(`Profundidad inválida: ${config.depth}`);
    }

    // Ordenar por jerarquía (padres primero)
    const sortedLocations = sortLocationsByHierarchy(locations);

    // Agrupar por tipo
    const byType = groupLocationsByType(sortedLocations);

    // Calcular estadísticas
    const calc = calculateLocations(config);

    // Generar preview
    const preview = this.generatePreview(sortedLocations, byType);

    return {
      locations: sortedLocations,
      byType,
      statistics: {
        total: sortedLocations.length,
        byType: {
          rows: byType.rows.length,
          racks: byType.racks.length,
          levels: byType.levels.length,
          positions: byType.positions.length,
        },
        depth: config.depth,
        estimatedCreationTime: estimateCreationTime(sortedLocations.length),
      },
      preview,
    };
  }

  /**
   * Genera ubicaciones con profundidad 1: SALON → POSITION
   * 
   * @param config - Configuración
   * @returns Array de ubicaciones generadas
   */
  private generateDepth1(config: BatchCreationConfig): GeneratedLocation[] {
    const { parentSalonId, parentSalonName, dimensions, customerId } = config;
    const { positions } = dimensions;
    const locations: GeneratedLocation[] = [];

    const positionPrefix = this.getPrefixForLevel(config, 'position');

    // Generar posiciones directamente bajo el salón
    for (let p = 1; p <= positions; p++) {
      const name = this.buildLocationName(
        parentSalonName,
        positionPrefix,
        p,
        config
      );

      locations.push({
        name,
        display_name: name,
        type: 'POSITION-FRONT',
        parent_id: parentSalonId,
        parent_name: parentSalonName,
        indices: { positionIndex: p },
        level: 1,
        path: [parentSalonName, name],
        customer_id: customerId,
      });
    }

    return locations;
  }

  /**
   * Genera ubicaciones con profundidad 2: SALON → ROW → POSITION
   * 
   * @param config - Configuración
   * @returns Array de ubicaciones generadas
   */
  private generateDepth2(config: BatchCreationConfig): GeneratedLocation[] {
    const { parentSalonId, parentSalonName, dimensions, customerId } = config;
    const { rows = 0, positions } = dimensions;
    const locations: GeneratedLocation[] = [];

    const rowPrefix = this.getPrefixForLevel(config, 'row');
    const positionPrefix = this.getPrefixForLevel(config, 'position');

    // Generar filas
    for (let f = 1; f <= rows; f++) {
      const rowName = this.buildLocationName(
        parentSalonName,
        rowPrefix,
        f,
        config
      );

      const row: GeneratedLocation = {
        name: rowName,
        display_name: rowName,
        type: 'ROW',
        parent_id: parentSalonId,
        parent_name: parentSalonName,
        indices: { rowIndex: f },
        level: 1,
        path: [parentSalonName, rowName],
        customer_id: customerId,
      };

      locations.push(row);

      // Generar posiciones bajo cada fila
      for (let p = 1; p <= positions; p++) {
        const positionName = this.buildLocationName(
          rowName,
          positionPrefix,
          p,
          config
        );

        locations.push({
          name: positionName,
          display_name: positionName,
          type: 'POSITION-FRONT',
          parent_name: rowName,
          // parent_id se asignará después de creación del padre
          indices: { rowIndex: f, positionIndex: p },
          level: 2,
          path: [parentSalonName, rowName, positionName],
          customer_id: customerId,
        });
      }
    }

    return locations;
  }

  /**
   * Genera ubicaciones con profundidad 3: SALON → ROW → RACK → POSITION
   * 
   * @param config - Configuración
   * @returns Array de ubicaciones generadas
   */
  private generateDepth3(config: BatchCreationConfig): GeneratedLocation[] {
    const { parentSalonId, parentSalonName, dimensions, customerId } = config;
    const { rows = 0, racks = 0, positions } = dimensions;
    const locations: GeneratedLocation[] = [];

    const rowPrefix = this.getPrefixForLevel(config, 'row');
    const rackPrefix = this.getPrefixForLevel(config, 'rack');
    const positionPrefix = this.getPrefixForLevel(config, 'position');

    // Generar filas
    for (let f = 1; f <= rows; f++) {
      const rowName = this.buildLocationName(
        parentSalonName,
        rowPrefix,
        f,
        config
      );

      const row: GeneratedLocation = {
        name: rowName,
        display_name: rowName,
        type: 'ROW',
        parent_id: parentSalonId,
        parent_name: parentSalonName,
        indices: { rowIndex: f },
        level: 1,
        path: [parentSalonName, rowName],
        customer_id: customerId,
      };

      locations.push(row);

      // Generar racks bajo cada fila
      for (let r = 1; r <= racks; r++) {
        const rackName = this.buildLocationName(
          rowName,
          rackPrefix,
          r,
          config
        );

        const rack: GeneratedLocation = {
          name: rackName,
          display_name: rackName,
          type: 'RACK-FRONT',
          parent_name: rowName,
          indices: { rowIndex: f, rackIndex: r },
          level: 2,
          path: [parentSalonName, rowName, rackName],
          customer_id: customerId,
        };

        locations.push(rack);

        // Generar posiciones bajo cada rack
        for (let p = 1; p <= positions; p++) {
          const positionName = this.buildLocationName(
            rackName,
            positionPrefix,
            p,
            config
          );

          locations.push({
            name: positionName,
            display_name: positionName,
            type: 'POSITION-FRONT',
            parent_name: rackName,
            indices: { rowIndex: f, rackIndex: r, positionIndex: p },
            level: 3,
            path: [parentSalonName, rowName, rackName, positionName],
            customer_id: customerId,
          });
        }
      }
    }

    return locations;
  }

  /**
   * Genera ubicaciones con profundidad 4: SALON → ROW → RACK → LEVEL → POSITION
   * 
   * @param config - Configuración
   * @returns Array de ubicaciones generadas
   */
  private generateDepth4(config: BatchCreationConfig): GeneratedLocation[] {
    const { parentSalonId, parentSalonName, dimensions, customerId } = config;
    const { rows = 0, racks = 0, levels = 0, positions } = dimensions;
    const locations: GeneratedLocation[] = [];

    const rowPrefix = this.getPrefixForLevel(config, 'row');
    const rackPrefix = this.getPrefixForLevel(config, 'rack');
    const levelPrefix = this.getPrefixForLevel(config, 'level');
    const positionPrefix = this.getPrefixForLevel(config, 'position');

    // Generar filas
    for (let f = 1; f <= rows; f++) {
      const rowName = this.buildLocationName(
        parentSalonName,
        rowPrefix,
        f,
        config
      );

      const row: GeneratedLocation = {
        name: rowName,
        display_name: rowName,
        type: 'ROW',
        parent_id: parentSalonId,
        parent_name: parentSalonName,
        indices: { rowIndex: f },
        level: 1,
        path: [parentSalonName, rowName],
        customer_id: customerId,
      };

      locations.push(row);

      // Generar racks bajo cada fila
      for (let r = 1; r <= racks; r++) {
        const rackName = this.buildLocationName(
          rowName,
          rackPrefix,
          r,
          config
        );

        const rack: GeneratedLocation = {
          name: rackName,
          display_name: rackName,
          type: 'RACK-FRONT',
          parent_name: rowName,
          indices: { rowIndex: f, rackIndex: r },
          level: 2,
          path: [parentSalonName, rowName, rackName],
          customer_id: customerId,
        };

        locations.push(rack);

        // Generar niveles bajo cada rack
        for (let l = 1; l <= levels; l++) {
          const levelName = this.buildLocationName(
            rackName,
            levelPrefix,
            l,
            config
          );

          const levelObj: GeneratedLocation = {
            name: levelName,
            display_name: levelName,
            type: 'LEVEL-FRONT',
            parent_name: rackName,
            indices: { rowIndex: f, rackIndex: r, levelIndex: l },
            level: 3,
            path: [parentSalonName, rowName, rackName, levelName],
            customer_id: customerId,
          };

          locations.push(levelObj);

          // Generar posiciones bajo cada nivel
          for (let p = 1; p <= positions; p++) {
            const positionName = this.buildLocationName(
              levelName,
              positionPrefix,
              p,
              config
            );

            locations.push({
              name: positionName,
              display_name: positionName,
              type: 'POSITION-FRONT',
              parent_name: levelName,
              indices: {
                rowIndex: f,
                rackIndex: r,
                levelIndex: l,
                positionIndex: p,
              },
              level: 4,
              path: [
                parentSalonName,
                rowName,
                rackName,
                levelName,
                positionName,
              ],
              customer_id: customerId,
            });
          }
        }
      }
    }

    return locations;
  }

  /**
   * Construye el nombre de una ubicación
   * Formato: {PADRE}-{PREFIJO}{CONSECUTIVO}
   * 
   * @param parentName - Nombre del padre
   * @param prefix - Prefijo del nivel
   * @param consecutive - Número consecutivo (1-based)
   * @param config - Configuración (para formato)
   * @returns Nombre completo de la ubicación
   */
  private buildLocationName(
    parentName: string,
    prefix: string,
    consecutive: number,
    config: BatchCreationConfig
  ): string {
    const format = config.consecutiveFormat || 'numeric';
    const paddingDigits = config.paddingDigits || 2;

    const formattedConsecutive = formatConsecutive(
      consecutive,
      format,
      paddingDigits
    );

    return `${parentName}-${prefix}${formattedConsecutive}`;
  }

  /**
   * Obtiene el prefijo efectivo para un nivel
   * 
   * @param config - Configuración
   * @param level - Nivel (row, rack, level, position)
   * @returns Prefijo a usar
   */
  private getPrefixForLevel(
    config: BatchCreationConfig,
    level: 'row' | 'rack' | 'level' | 'position'
  ): string {
    const customPrefix = config.prefixes?.[level];
    const defaultPrefix = DEFAULT_PREFIXES[level];

    return getEffectivePrefix(customPrefix, defaultPrefix);
  }

  /**
   * Genera preview de nomenclaturas
   * 
   * @param locations - Ubicaciones generadas
   * @param byType - Ubicaciones agrupadas por tipo
   * @returns Preview con ejemplos
   */
  private generatePreview(
    locations: GeneratedLocation[],
    byType: ReturnType<typeof groupLocationsByType>
  ) {
    return {
      first: {
        row: byType.rows[0]?.name,
        rack: byType.racks[0]?.name,
        level: byType.levels[0]?.name,
        position: byType.positions[0]?.name || locations[0]?.name,
      },
      last: {
        row: byType.rows[byType.rows.length - 1]?.name,
        rack: byType.racks[byType.racks.length - 1]?.name,
        level: byType.levels[byType.levels.length - 1]?.name,
        position:
          byType.positions[byType.positions.length - 1]?.name ||
          locations[locations.length - 1]?.name,
      },
      sample: {
        rows: this.getSample(byType.rows, 3),
        racks: this.getSample(byType.racks, 3),
        levels: this.getSample(byType.levels, 3),
        positions: this.getSample(byType.positions, 3),
      },
    };
  }

  /**
   * Obtiene una muestra de ubicaciones
   * 
   * @param locations - Array de ubicaciones
   * @param count - Cantidad de muestras
   * @returns Array de nombres
   */
  private getSample(locations: GeneratedLocation[], count: number): string[] {
    if (locations.length <= count) {
      return locations.map((l) => l.name);
    }

    const step = Math.floor(locations.length / count);
    const sample: string[] = [];

    for (let i = 0; i < count && i * step < locations.length; i++) {
      sample.push(locations[i * step].name);
    }

    return sample;
  }

  /**
   * Valida la configuración antes de generar
   * 
   * @param config - Configuración a validar
   * @throws Error si la configuración es inválida
   */
  private validateConfig(config: BatchCreationConfig): void {
    if (!config.parentSalonId || config.parentSalonId <= 0) {
      throw new Error('parent_salon_id es requerido y debe ser mayor a 0');
    }

    if (!config.parentSalonName || config.parentSalonName.trim() === '') {
      throw new Error('parent_salon_name es requerido');
    }

    if (!config.depth || config.depth < 1 || config.depth > 4) {
      throw new Error('depth debe estar entre 1 y 4');
    }

    if (!config.customerId || config.customerId <= 0) {
      throw new Error('customer_id es requerido y debe ser mayor a 0');
    }

    // Validar dimensiones según profundidad
    const { dimensions, depth } = config;

    if (!dimensions.positions || dimensions.positions < 1) {
      throw new Error('positions es requerido y debe ser al menos 1');
    }

    if (depth >= 2 && (!dimensions.rows || dimensions.rows < 1)) {
      throw new Error(
        'rows es requerido para profundidad >= 2 y debe ser al menos 1'
      );
    }

    if (depth >= 3 && (!dimensions.racks || dimensions.racks < 1)) {
      throw new Error(
        'racks es requerido para profundidad >= 3 y debe ser al menos 1'
      );
    }

    if (depth === 4 && (!dimensions.levels || dimensions.levels < 1)) {
      throw new Error(
        'levels es requerido para profundidad = 4 y debe ser al menos 1'
      );
    }
  }
}
